import { getDownload, get } from '../utils/api';
import { IS_DEVELOPMENT } from '../constants/environment';

interface DownloadPDFParams {
  organizationId: string;
  propertyId: string;
  portalUserId: string;
  filename: string;
  options?: Record<string, any>;
}

interface CheckFormExistsParams {
  organizationId: string;
  propertyId: string;
  portalUserId: string;
  options?: Record<string, any>;
}

interface CheckCustomerAffordaPortalExistsParams {
  organizationId: string;
  propertyId: string;
  customerId: string;
  options?: Record<string, any>;
}

export class AffordaPortalFormsService {
  /**
   * Downloads application details PDF for a specific portal user.
   * @param {Object} params - Parameters for the download
   * @param {string} params.organizationId - The organization ID
   * @param {string} params.propertyId - The property ID
   * @param {string} params.portalUserId - The portal user ID
   * @param {Object} [params.options] - Optional request configuration
   * @returns {Promise<Blob>} The PDF file as a blob
   * @throws {Error} If portalUserId is not provided or if the request fails
   */
  async downloadApplicationDetailsPDF({
    organizationId,
    propertyId,
    portalUserId,
    filename,
    options = {},
  }: DownloadPDFParams): Promise<void> {
    if (!portalUserId) {
      throw new Error('portalUserId is required.');
    }

    const url = `/${organizationId}/${propertyId}/afforda-portal-forms/${portalUserId}/${filename}/pdf`;

    try {
      const response = await getDownload(url, filename, options);

      if (response.error) {
        if (IS_DEVELOPMENT) console.error(response.error);
        throw new Error(response.error);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) console.error('Error downloading PDF:', error);
      throw new Error(`Failed to download PDF: ${error.message || error}`);
    }
  }

  /**
   * Checks if form data exists for a specific portal user.
   * @param {Object} params - Parameters for checking form existence
   * @param {string} params.organizationId - The organization ID
   * @param {string} params.propertyId - The property ID
   * @param {string} params.portalUserId - The portal user ID
   * @param {Object} [params.options] - Optional request configuration
   * @returns {Promise<boolean>} True if form exists, false otherwise
   * @throws {Error} If portalUserId is not provided or if the request fails
   */
  async checkFormExists({
    organizationId,
    propertyId,
    portalUserId,
    options = {},
  }: CheckFormExistsParams): Promise<boolean> {
    if (!portalUserId) {
      throw new Error('portalUserId is required.');
    }

    const url = `/${organizationId}/${propertyId}/afforda-portal-forms/${portalUserId}/exists`;

    try {
      const response = await get(url, options);

      if (response.error) {
        if (IS_DEVELOPMENT) console.error(response.error);
        throw new Error(response.error);
      }

      return response.exists;
    } catch (error) {
      if (IS_DEVELOPMENT)
        console.error('Error checking form existence:', error);
      throw new Error(
        `Failed to check form existence: ${error.message || error}`,
      );
    }
  }

  /**
   * Whether afforda portal data exists for a household customer (e.g. MyFortress).
   * GET .../afforda-portal-forms/customer/{customerId}/exists
   */
  async checkCustomerAffordaPortalExists({
    organizationId,
    propertyId,
    customerId,
    options = {},
  }: CheckCustomerAffordaPortalExistsParams): Promise<boolean> {
    if (!customerId) {
      throw new Error('customerId is required.');
    }

    const url = `/${organizationId}/${propertyId}/afforda-portal-forms/customer/${customerId}/exists`;

    try {
      const response = await get(url, options);

      if (response.error) {
        if (IS_DEVELOPMENT) console.error(response.error);
        throw new Error(response.error);
      }

      return Boolean(response.exists);
    } catch (error) {
      if (IS_DEVELOPMENT)
        console.error('Error checking customer afforda portal exists:', error);
      throw new Error(
        `Failed to check customer afforda portal exists: ${
          error.message || error
        }`,
      );
    }
  }
}

export default AffordaPortalFormsService;
